import { useEffect, useRef, type RefObject } from 'react';
import type { HandleType } from '@xyflow/react';
import { proxy } from 'valtio';
import {
  resolveDefaultMagneticHandlePoint,
  resolveMagneticHandleConfig,
  resolveMagnetizedHandlePoint,
  type MagneticHandleConfig,
  type MagneticHandlePoint,
  type MagneticHandleSide,
} from './model';

export type NodeSnapshot = {
  dropTarget: boolean;
  magnetPoint: MagneticHandlePoint;
  magnetSide: MagneticHandleSide | null;
  nodeHeight: number;
  nodeWidth: number;
};

type PointerSample = {
  clientX: number;
  clientY: number;
  target: EventTarget | null;
};

type RegisteredNode = {
  config: MagneticHandleConfig;
  lastMeasured: { height: number; width: number } | null;
  lastRect: DOMRect | null;
  lastRectZoom: number;
  nodeId: string;
  nodeRef: RefObject<HTMLDivElement | null>;
};

type PointerCandidate = {
  distanceFromPointer: number;
  magnetPoint: MagneticHandlePoint;
  magnetSide: MagneticHandleSide | null;
  nodeHeight: number;
  nodeId: string;
  nodeWidth: number;
};

type MagneticCandidateSnapshot = {
  magnetPoint: MagneticHandlePoint;
  magnetSide: MagneticHandleSide | null;
  nodeHeight: number;
  nodeId: string;
  nodeWidth: number;
};

export type CanConnectDropTargetChecker = (params: {
  dropTargetNodeId: string;
  startHandleType: HandleType;
  startNodeId: string;
}) => boolean;

export type MagneticHandlesStoreState = {
  canConnectDropTarget: boolean;
  connectingFromHandleType: HandleType | null;
  connectingFromNodeId: string | null;
  dropTargetNodeId: string | null;
  magnetNodeId: string | null;
  nodeSnapshots: Record<string, NodeSnapshot>;
};

export type MagneticHandlesSession = {
  store: MagneticHandlesStoreState;
  attach: () => () => void;
  finishConnection: () => void;
  getCanConnectDropTarget: () => boolean;
  getCurrentDropTargetNodeId: () => string | null;
  registerNode: (options: {
    config?: Partial<MagneticHandleConfig>;
    nodeId: string;
    nodeRef: RefObject<HTMLDivElement | null>;
  }) => () => void;
  setCanConnectDropTargetChecker: (checker: CanConnectDropTargetChecker | null) => void;
  setZoom: (zoom: number) => void;
  startConnection: (state: { handleType: HandleType; nodeId: string }) => void;
  updateNodeConfig: (nodeId: string, config?: Partial<MagneticHandleConfig>) => void;
};

export const DEFAULT_MAGNETIC_NODE_SNAPSHOT: NodeSnapshot = {
  dropTarget: false,
  magnetPoint: { x: 0, y: 0 },
  magnetSide: null,
  nodeHeight: 148,
  nodeWidth: 260,
};

const NODE_ATTR = 'data-canvas-magnetic-node-id';

function sameSnapshot(left: NodeSnapshot, right: NodeSnapshot) {
  return (
    left.dropTarget === right.dropTarget
    && left.magnetPoint.x === right.magnetPoint.x
    && left.magnetPoint.y === right.magnetPoint.y
    && left.magnetSide === right.magnetSide
    && left.nodeHeight === right.nodeHeight
    && left.nodeWidth === right.nodeWidth
  );
}

function resolveNodeIdFromTarget(target: EventTarget | null) {
  if (!(target instanceof Element)) {
    return null;
  }

  return target.closest(`[${NODE_ATTR}]`)?.getAttribute(NODE_ATTR) ?? null;
}

function resolveAllowedMagnetSide(handleType: HandleType | null): MagneticHandleSide | null {
  if (handleType === 'source') {
    return 'left';
  }

  if (handleType === 'target') {
    return 'right';
  }

  return null;
}

function quickReject(node: RegisteredNode, pointer: PointerSample, zoom: number): boolean {
  const rect = node.lastRect;
  if (!rect) {
    return false;
  }

  const safeZoom = zoom > 0 ? zoom : 1;
  const config = node.config;
  const hPad = (config.initialOffset + config.magnetRadius) * safeZoom;
  const vPad = config.magnetRadius * safeZoom;

  return (
    pointer.clientX < rect.left - hPad
    || pointer.clientX > rect.right + hPad
    || pointer.clientY < rect.top - vPad
    || pointer.clientY > rect.bottom + vPad
  );
}

export function createMagneticHandlesSession(): MagneticHandlesSession {
  const store = proxy<MagneticHandlesStoreState>({
    canConnectDropTarget: false,
    connectingFromHandleType: null,
    connectingFromNodeId: null,
    dropTargetNodeId: null,
    magnetNodeId: null,
    nodeSnapshots: {},
  });

  const nodes = new Map<string, RegisteredNode>();
  const elementToNodeId = new WeakMap<Element, string>();
  let zoom = 1;
  let resizeObserver: ResizeObserver | null = null;
  let rafId = 0;
  let latestPointerSample: PointerSample | null = null;
  let canConnectDropTargetChecker: CanConnectDropTargetChecker | null = null;
  let lastDropTargetCheckKey: string | null = null;

  const getSnapshot = (nodeId: string) => store.nodeSnapshots[nodeId] ?? DEFAULT_MAGNETIC_NODE_SNAPSHOT;

  const patchSnapshot = (nodeId: string, patch: Partial<NodeSnapshot>) => {
    const current = getSnapshot(nodeId);
    const next = {
      ...current,
      ...patch,
    };

    if (sameSnapshot(current, next)) {
      return;
    }

    store.nodeSnapshots[nodeId] = next;
  };

  const removeSnapshot = (nodeId: string) => {
    if (!(nodeId in store.nodeSnapshots)) {
      return;
    }

    delete store.nodeSnapshots[nodeId];
  };

  const syncMetrics = (nodeId: string) => {
    const entry = nodes.get(nodeId);
    const element = entry?.nodeRef.current;

    if (!entry || !element) {
      return null;
    }

    const width = element.clientWidth || DEFAULT_MAGNETIC_NODE_SNAPSHOT.nodeWidth;
    const height = element.clientHeight || DEFAULT_MAGNETIC_NODE_SNAPSHOT.nodeHeight;
    const snapshot = getSnapshot(nodeId);
    const defaultSide = snapshot.magnetSide ?? 'left';
    const defaultPoint = resolveDefaultMagneticHandlePoint(width, height, defaultSide, entry.config);

    patchSnapshot(nodeId, {
      magnetPoint: snapshot.magnetSide ? snapshot.magnetPoint : defaultPoint,
      nodeHeight: height,
      nodeWidth: width,
    });

    return {
      element,
      height,
      width,
    };
  };

  const applyDropTargetNode = (nextDropTargetNodeId: string | null) => {
    const previousDropTargetNodeId = store.dropTargetNodeId;
    if (previousDropTargetNodeId === nextDropTargetNodeId) {
      return;
    }

    if (previousDropTargetNodeId) {
      patchSnapshot(previousDropTargetNodeId, { dropTarget: false });
    }

    store.dropTargetNodeId = nextDropTargetNodeId;

    if (nextDropTargetNodeId) {
      patchSnapshot(nextDropTargetNodeId, { dropTarget: true });
    }
  };

  const applyMagnetCandidate = (candidate: MagneticCandidateSnapshot | null) => {
    const previousMagnetNodeId = store.magnetNodeId;
    const nextMagnetNodeId = candidate?.magnetSide ? candidate.nodeId : null;

    if (previousMagnetNodeId && previousMagnetNodeId !== nextMagnetNodeId) {
      const currentSnapshot = getSnapshot(previousMagnetNodeId);
      patchSnapshot(previousMagnetNodeId, {
        magnetPoint: resolveDefaultMagneticHandlePoint(
          currentSnapshot.nodeWidth,
          currentSnapshot.nodeHeight,
          currentSnapshot.magnetSide ?? 'left',
          nodes.get(previousMagnetNodeId)?.config,
        ),
        magnetSide: null,
      });
    }

    store.magnetNodeId = nextMagnetNodeId;

    if (!candidate) {
      return;
    }

    patchSnapshot(candidate.nodeId, {
      magnetPoint: candidate.magnetPoint,
      magnetSide: candidate.magnetSide,
      nodeHeight: candidate.nodeHeight,
      nodeWidth: candidate.nodeWidth,
    });
  };

  const resetCanConnectDropTarget = () => {
    lastDropTargetCheckKey = null;
    store.canConnectDropTarget = false;
  };

  const clearTransientState = () => {
    applyDropTargetNode(null);
    applyMagnetCandidate(null);
    resetCanConnectDropTarget();
  };

  const updateCanConnectDropTarget = ({
    dropTargetNodeId,
    startHandleType,
    startNodeId,
  }: {
    dropTargetNodeId: string | null;
    startHandleType: HandleType;
    startNodeId: string;
  }) => {
    if (!dropTargetNodeId || !canConnectDropTargetChecker) {
      resetCanConnectDropTarget();
      return;
    }

    const nextCheckKey = `${startHandleType}:${startNodeId}:${dropTargetNodeId}`;
    if (nextCheckKey === lastDropTargetCheckKey) {
      return;
    }

    lastDropTargetCheckKey = nextCheckKey;
    const canConnectDropTarget = canConnectDropTargetChecker({
      dropTargetNodeId,
      startHandleType,
      startNodeId,
    });

    store.canConnectDropTarget = canConnectDropTarget;
  };

  const resolveCandidateForNode = (nodeId: string, pointer: PointerSample, allowedSide: MagneticHandleSide): PointerCandidate | null => {
    const entry = nodes.get(nodeId);
    const metrics = syncMetrics(nodeId);

    if (!entry || !metrics) {
      return null;
    }

    const rect = metrics.element.getBoundingClientRect();
    entry.lastRect = rect;
    entry.lastRectZoom = zoom;
    const safeZoom = zoom > 0 ? zoom : 1;
    const horizontalPadding = (entry.config.initialOffset + entry.config.magnetRadius) * safeZoom;
    const verticalPadding = entry.config.magnetRadius * safeZoom;

    if (
      pointer.clientX < rect.left - horizontalPadding
      || pointer.clientX > rect.right + horizontalPadding
      || pointer.clientY < rect.top - verticalPadding
      || pointer.clientY > rect.bottom + verticalPadding
    ) {
      return null;
    }

    const result = resolveMagnetizedHandlePoint({
      allowedSide,
      clientX: pointer.clientX,
      clientY: pointer.clientY,
      config: entry.config,
      nodeHeight: metrics.height,
      nodeWidth: metrics.width,
      rect,
      zoom: safeZoom,
    });

    return {
      distanceFromPointer: Math.hypot(pointer.clientX - rect.left - result.point.x * safeZoom, pointer.clientY - rect.top - result.point.y * safeZoom),
      magnetPoint: result.point,
      magnetSide: result.side,
      nodeHeight: metrics.height,
      nodeId,
      nodeWidth: metrics.width,
    };
  };

  const flushPointerSample = () => {
    rafId = 0;

    const pointer = latestPointerSample;
    latestPointerSample = null;
    if (!pointer) {
      return;
    }

    const { connectingFromHandleType, connectingFromNodeId } = store;
    const targetedNodeId = resolveNodeIdFromTarget(pointer.target);

    if (!connectingFromNodeId || !connectingFromHandleType) {
      clearTransientState();
      return;
    }

    const allowedSide = resolveAllowedMagnetSide(connectingFromHandleType);
    if (!allowedSide) {
      clearTransientState();
      return;
    }

    let hoveredCandidate: PointerCandidate | null = null;
    if (targetedNodeId && targetedNodeId !== connectingFromNodeId) {
      hoveredCandidate = resolveCandidateForNode(targetedNodeId, pointer, allowedSide);
    }

    let bestCandidate: PointerCandidate | null = null;
    for (const [nodeId, node] of nodes.entries()) {
      if (nodeId === connectingFromNodeId) {
        continue;
      }

      if (quickReject(node, pointer, zoom)) {
        continue;
      }

      const candidate = resolveCandidateForNode(nodeId, pointer, allowedSide);
      if (!candidate?.magnetSide) {
        continue;
      }

      if (!bestCandidate || candidate.distanceFromPointer < bestCandidate.distanceFromPointer) {
        bestCandidate = candidate;
      }
    }

    const dropTargetNodeId = bestCandidate?.nodeId ?? hoveredCandidate?.nodeId ?? null;
    applyMagnetCandidate(bestCandidate);
    applyDropTargetNode(dropTargetNodeId);
    updateCanConnectDropTarget({
      dropTargetNodeId,
      startHandleType: connectingFromHandleType,
      startNodeId: connectingFromNodeId,
    });
  };

  const schedulePointerFlush = (pointer: PointerSample) => {
    latestPointerSample = pointer;
    if (rafId !== 0) {
      return;
    }

    rafId = window.requestAnimationFrame(flushPointerSample);
  };

  const ensureResizeObserver = () => {
    if (resizeObserver || typeof ResizeObserver === 'undefined') {
      return;
    }

    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const nodeId = elementToNodeId.get(entry.target);
        if (!nodeId) {
          continue;
        }

        const registered = nodes.get(nodeId);
        if (!registered) {
          continue;
        }

        const width = entry.contentRect.width || entry.target.clientWidth || DEFAULT_MAGNETIC_NODE_SNAPSHOT.nodeWidth;
        const height = entry.contentRect.height || entry.target.clientHeight || DEFAULT_MAGNETIC_NODE_SNAPSHOT.nodeHeight;

        registered.lastMeasured = { width, height };
        registered.lastRect = entry.target.getBoundingClientRect();
        registered.lastRectZoom = zoom;

        const currentSnapshot = getSnapshot(nodeId);

        patchSnapshot(nodeId, {
          magnetPoint: currentSnapshot.magnetSide
            ? currentSnapshot.magnetPoint
            : resolveDefaultMagneticHandlePoint(width, height, 'left', registered.config),
          nodeHeight: height,
          nodeWidth: width,
        });
      }
    });
  };

  return {
    store,

    attach() {
      const handlePointerMove = (event: PointerEvent) => {
        schedulePointerFlush({
          clientX: event.clientX,
          clientY: event.clientY,
          target: event.target,
        });
      };

      const handlePointerLeave = () => {
        latestPointerSample = null;
        if (rafId !== 0) {
          window.cancelAnimationFrame(rafId);
          rafId = 0;
        }
        clearTransientState();
      };

      window.addEventListener('pointermove', handlePointerMove, { passive: true });
      window.addEventListener('pointerleave', handlePointerLeave);
      window.addEventListener('blur', handlePointerLeave);

      return () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerleave', handlePointerLeave);
        window.removeEventListener('blur', handlePointerLeave);
      };
    },

    finishConnection() {
      store.connectingFromHandleType = null;
      store.connectingFromNodeId = null;
      clearTransientState();
    },

    getCanConnectDropTarget() {
      return store.canConnectDropTarget;
    },

    getCurrentDropTargetNodeId() {
      return store.dropTargetNodeId;
    },

    registerNode({ config, nodeId, nodeRef }) {
      const resolvedConfig = resolveMagneticHandleConfig(config);
      nodes.set(nodeId, {
        config: resolvedConfig,
        lastMeasured: null,
        lastRect: null,
        lastRectZoom: 0,
        nodeId,
        nodeRef,
      });

      const element = nodeRef.current;
      if (element) {
        element.setAttribute(NODE_ATTR, nodeId);
        elementToNodeId.set(element, nodeId);
        ensureResizeObserver();
        resizeObserver?.observe(element);
        const width = element.clientWidth || DEFAULT_MAGNETIC_NODE_SNAPSHOT.nodeWidth;
        const height = element.clientHeight || DEFAULT_MAGNETIC_NODE_SNAPSHOT.nodeHeight;
        const entry = nodes.get(nodeId);
        if (entry) {
          entry.lastMeasured = { width, height };
          entry.lastRect = element.getBoundingClientRect();
          entry.lastRectZoom = zoom;
        }
        patchSnapshot(nodeId, {
          magnetPoint: resolveDefaultMagneticHandlePoint(width, height, 'left', resolvedConfig),
          nodeHeight: height,
          nodeWidth: width,
        });
      }

      return () => {
        const currentElement = nodes.get(nodeId)?.nodeRef.current;
        if (currentElement) {
          resizeObserver?.unobserve(currentElement);
          currentElement.removeAttribute(NODE_ATTR);
        }

        nodes.delete(nodeId);
        removeSnapshot(nodeId);

        const removingDropTarget = store.dropTargetNodeId === nodeId;
        if (removingDropTarget) {
          lastDropTargetCheckKey = null;
        }
        store.dropTargetNodeId = store.dropTargetNodeId === nodeId ? null : store.dropTargetNodeId;
        store.magnetNodeId = store.magnetNodeId === nodeId ? null : store.magnetNodeId;
        store.canConnectDropTarget = removingDropTarget ? false : store.canConnectDropTarget;
      };
    },

    setCanConnectDropTargetChecker(checker) {
      canConnectDropTargetChecker = checker;
      resetCanConnectDropTarget();
    },

    setZoom(nextZoom) {
      const prevZoom = zoom;
      zoom = nextZoom > 0 ? nextZoom : 1;
      if (prevZoom !== zoom) {
        for (const entry of nodes.values()) {
          entry.lastRect = null;
        }
      }
    },

    startConnection(state) {
      store.connectingFromHandleType = state.handleType;
      store.connectingFromNodeId = state.nodeId;
      clearTransientState();
    },

    updateNodeConfig(nodeId, config) {
      const entry = nodes.get(nodeId);
      if (!entry) {
        return;
      }

      entry.config = resolveMagneticHandleConfig(config);
      syncMetrics(nodeId);
    },
  };
}

export function useMagneticHandlesSession(initialZoom = 1) {
  const sessionRef = useRef<MagneticHandlesSession | null>(null);

  if (sessionRef.current === null) {
    sessionRef.current = createMagneticHandlesSession();
  }

  const session = sessionRef.current;

  useEffect(() => {
    session.setZoom(initialZoom);
  }, [initialZoom, session]);

  useEffect(() => session.attach(), [session]);

  return session;
}
